import React, { useState, useEffect, useCallback } from 'react';
import { REAUTH_REQUIRED_EVENT, resolveReAuth, rejectReAuth } from '../api/reAuthGate';
import { login } from '../api/authApi';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../hooks/useTheme';

const SessionExpiredModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { theme } = useTheme();
  // Capture username when the modal opens (user object may be cleared on reject)
  const [username, setUsername] = useState('');

  useEffect(() => {
    const handle = () => {
      setUsername(user?.username ?? user?.displayName ?? '');
      setPassword('');
      setError('');
      setOpen(true);
    };
    window.addEventListener(REAUTH_REQUIRED_EVENT, handle);
    return () => window.removeEventListener(REAUTH_REQUIRED_EVENT, handle);
  }, [user]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      setOpen(false);
      resolveReAuth();
    } catch (err: any) {
      setError(err.message ?? 'Ошибка входа');
    } finally {
      setLoading(false);
    }
  }, [username, password]);

  const handleCancel = useCallback(() => {
    setOpen(false);
    rejectReAuth();
  }, []);

  if (!open) return null;

  const isDark = theme.name === 'dark';
  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 10000,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const box: React.CSSProperties = {
    background: theme.codePanel?.background ?? (isDark ? '#1e2433' : '#fff'),
    border: `1px solid ${theme.toolbar.buttonBorder}`,
    borderRadius: 12,
    padding: '32px 28px',
    width: '100%', maxWidth: 380,
    boxShadow: '0 8px 32px rgba(0,0,0,0.32)',
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 12px',
    borderRadius: 6, border: `1px solid ${theme.toolbar.buttonBorder}`,
    background: theme.toolbar.buttonBackground,
    color: theme.toolbar.text, fontSize: 14,
    outline: 'none', boxSizing: 'border-box', marginTop: 6,
  };
  const btnPrimary: React.CSSProperties = {
    width: '100%', padding: '10px 0',
    borderRadius: 6, border: 'none',
    background: isDark ? '#3b82f6' : '#2563eb',
    color: '#fff', fontSize: 14, fontWeight: 600,
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.7 : 1,
  };
  const btnCancel: React.CSSProperties = {
    width: '100%', padding: '9px 0',
    borderRadius: 6, border: `1px solid ${theme.toolbar.buttonBorder}`,
    background: 'transparent',
    color: theme.toolbar.text, fontSize: 14,
    cursor: 'pointer', marginTop: 8,
  };

  return (
    <div style={overlay}>
      <div style={box}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: theme.toolbar.text, marginBottom: 6 }}>
            Сессия истекла
          </div>
          <div style={{ fontSize: 13, color: theme.toolbar.text, opacity: 0.65 }}>
            Введите пароль, чтобы продолжить работу без потери данных.
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: theme.toolbar.text, opacity: 0.7 }}>Логин</label>
            <input
              style={{ ...inputStyle, opacity: 0.6, cursor: 'default' }}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
            />
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 12, color: theme.toolbar.text, opacity: 0.7 }}>Пароль</label>
            <input
              type="password"
              style={inputStyle}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</div>
          )}

          <button type="submit" style={btnPrimary} disabled={loading}>
            {loading ? 'Вход...' : 'Войти и продолжить'}
          </button>
          <button type="button" style={btnCancel} onClick={handleCancel}>
            Выйти из аккаунта
          </button>
        </form>
      </div>
    </div>
  );
};

export default SessionExpiredModal;
